import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Share2, Facebook, Mail, Heart, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from './PageHeader';
import { HelpModal } from './HelpModal';
import { Footer } from './Footer';
import { Textarea } from './ui/textarea';
import { ENErrorDisplay } from './ENErrorDisplay';
import type { ENError } from '../services/enAjaxSubmission';
import { SKIP_SHARE_VOICE_FOR_LANGUAGE, SKIP_SIGNUP_FOR_LANGUAGE } from '../config/constants';
import { trackConversion, trackScreenView } from '../utils/analytics';
import { submitCommentToEN } from '../services/enAjaxSubmission';
import { hasCommentField } from '../config/enFormConfig';

export function CompletionScreen() {
  const { t, i18n } = useTranslation('completion');
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [enErrors, setEnErrors] = useState<ENError[]>([]);
  const hasTrackedScreenView = useRef(false);
  
  // Get dynamic share URL (landing page URL without query params)
  const shareUrl = (() => {
    const url = new URL(window.location.href);
    return `${url.origin}${url.pathname}`;
  })();
  
  // Retrieve supporter name synchronously before any useEffect runs (before localStorage is cleared)
  const supporterName = (() => {
    try {
      const savedData = localStorage.getItem('consultationUser');
      if (savedData) {
        const userData = JSON.parse(savedData) as { firstName?: string; lastName?: string };
        if (userData.firstName) {
          return userData.firstName.trim();
        }
      }
    } catch (e) {
      console.error('[CompletionScreen] Failed to parse supporter name:', e);
    }
    return '';
  })();
  
  // Default email for anonymous submissions
  const DEFAULT_EMAIL = 'anonymous-eu@noaddress.ea';
  
  // Get email from sessionStorage or use default
  const getEmail = (): string => {
    const storedEmail = sessionStorage.getItem('consultationEmail');
    return storedEmail || DEFAULT_EMAIL;
  };
  
  // Check if comment form should be shown (only if language has comment field)
  const showCommentForm = hasCommentField(i18n.language);

  // Get consultation ID from root element
  const consultationId = (() => {
    const rootElement = document.getElementById('consultation-wizard-root');
    return rootElement?.getAttribute('data-consultation-id') || 'unknown';
  })();

  // Track screen view and conversion event when completion screen loads (only once)
  useEffect(() => {
    if (!hasTrackedScreenView.current) {
      trackScreenView('completion', consultationId, 4);
      trackConversion(consultationId);
      hasTrackedScreenView.current = true;
    }
  }, [consultationId]);

  // Cleanup localStorage on mount (consultation is complete)
  useEffect(() => {
    // Clear consultation data from localStorage after successful completion
    localStorage.removeItem('consultationUser');
    localStorage.removeItem('ciwf-wizard-storage');
    localStorage.removeItem('consultationProgress');
  }, []);
  
  // Cleanup: Clear email from sessionStorage when component unmounts
  useEffect(() => {
    return () => {
      sessionStorage.removeItem('consultationEmail');
    };
  }, []);

  const handleCommentSubmit = async () => {
    if (!comment.trim()) {
      toast.error('Please enter a comment before submitting');
      return;
    }

    console.log('[CompletionScreen] Comment submit initiated, comment length:', comment.length);
    
    setIsSubmitting(true);
    setEnErrors([]);
    
    try {
      // Get email from sessionStorage or use default
      const email = getEmail();
      
      console.log('[CompletionScreen] Submitting comment with email:', email.substring(0, 3) + '***');
      console.log('[CompletionScreen] Language:', i18n.language);
      console.log('[CompletionScreen] Calling submitCommentToEN...');
      
      // Submit comment and email to EN via AJAX
      const response = await submitCommentToEN(comment, email, i18n.language);
      
      console.log('[CompletionScreen] Response received:', response.success ? 'SUCCESS' : 'FAILED', response);
      
      if (!response.success) {
        // Handle errors
        if (response.errors && response.errors.length > 0) {
          console.error('[CompletionScreen] EN validation errors:', response.errors);
          setEnErrors(response.errors);
        } else {
          setEnErrors([{
            message: 'An unexpected error occurred. Please try again.',
          }]);
        }
        setIsSubmitting(false);
        return;
      }
      
      // Success - clear email from sessionStorage
      sessionStorage.removeItem('consultationEmail');
      
      setHasSubmitted(true);
      toast.success(t('feedback.thankYou'));
    } catch (err) {
      console.error('Failed to submit comment:', err);
      toast.error('Failed to submit comment. Please try again.');
      setEnErrors([{
        message: 'Failed to submit comment. Please try again.',
      }]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleShare = (platform: string) => {
    const shareMessage = t('sharing.shareMessage');
    const emailSubject = t('sharing.emailSubject');
    let emailBody = t('sharing.emailBody');
    
    // Replace placeholders in email body
    emailBody = emailBody.replace('{{landing.page}}', shareUrl);
    
    if (supporterName) {
      emailBody = emailBody.replace('{{user.name}}', supporterName);
    } else {
      // Remove the signature line if no supporter name is available
      emailBody = emailBody.replace('\n{{user.name}}', '');
    }
    
    switch (platform) {
      case 'facebook': {
        const facebookUrl = t('sharing.facebookUrl');
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(facebookUrl)}`, '_blank');
        break;
      }
      case 'twitter': {
        // Twitter/X share with both URL and text parameters
        const twitterUrl = t('sharing.twitterUrl');
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(twitterUrl)}&text=${encodeURIComponent(shareMessage)}`, '_blank');
        break;
      }
      case 'email':
        window.open(`mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`, '_blank');
        break;
    }
  };

  // Check if "Share your voice" feedback section should be hidden
  // Hidden if: signup was skipped (any method) OR SKIP_SHARE_VOICE_FOR_LANGUAGE matches
  const shouldHideShareVoice = () => {
    const currentLanguage = i18n.language.toLowerCase();
    
    // Check if signup was skipped via URL parameter
    const params = new URLSearchParams(window.location.search);
    if (params.get('skipSignup') === 'true') {
      return true;
    }
    
    // Check if signup was skipped via data attribute
    const rootElement = document.getElementById('consultation-wizard-root');
    if (rootElement?.getAttribute('data-skip-signup') === 'true') {
      return true;
    }
    
    // Check if signup was skipped via SKIP_SIGNUP_FOR_LANGUAGE env variable
    if (SKIP_SIGNUP_FOR_LANGUAGE) {
      const skipSignupLanguages = SKIP_SIGNUP_FOR_LANGUAGE.split(',').map((lang: string) => lang.trim().toLowerCase());
      if (skipSignupLanguages.includes(currentLanguage)) {
        return true;
      }
    }
    
    // Check explicit SKIP_SHARE_VOICE_FOR_LANGUAGE setting
    if (SKIP_SHARE_VOICE_FOR_LANGUAGE) {
      const skipShareVoiceLanguages = SKIP_SHARE_VOICE_FOR_LANGUAGE.split(',').map((lang: string) => lang.trim().toLowerCase());
      if (skipShareVoiceLanguages.includes(currentLanguage)) {
        return true;
      }
    }
    
    return false;
  };

  const hideShareVoice = shouldHideShareVoice();

  // Check if donation section should be hidden for English and Czech languages
  const shouldHideDonation = () => {
    const currentLanguage = i18n.language.toLowerCase();
    return currentLanguage === 'en' || currentLanguage === 'cs' || currentLanguage === 'es';
  };

  const hideDonation = shouldHideDonation();

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <HelpModal 
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
      />
      <PageHeader onHelpClick={() => setShowHelpModal(true)} />
      <main role="main" className="flex-1 flex items-center justify-center px-8 lg:px-12 py-4">
        <div className="max-w-4xl w-full text-center">
          <div className="mb-8">
          <CheckCircle2 className="w-20 h-20 text-accent mx-auto mb-6 flex-shrink-0" />
          <h1 className="mb-4 text-[24px] font-bold">{t('congratulations')}</h1>
          <p className="max-w-2xl mx-auto mb-6">
            {t('completionMessage')}
          </p>
          <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 px-6 py-3 mb-6">
            <p className="text-green-800">{t('submitted.title')}</p>
          </div>
        </div>

        {/* What Happens Next */}
        <div className="bg-amber-50 border border-amber-200 p-8 mb-8 text-left">
          <h2 className="mb-4 font-bold text-[20px]">{t('whatNext.title')}</h2>
          <p>
            {t('whatNext.description')}
          </p>
        </div>

        {/* Feedback/Comment Section - Hidden for specific languages and if comment field not configured */}
        {!hideShareVoice && showCommentForm && (
          <div className="bg-gray-50 border border-gray-200 p-8 mb-8 text-left">
            <div className="flex items-center gap-3 mb-4">
              <MessageSquare className="w-6 h-6 text-primary" />
              <h2 className="font-bold text-[20px]">{t('feedback.title')}</h2>
            </div>
            <p className="mb-4">
              {t('feedback.description')}
            </p>
            
            {!hasSubmitted ? (
              <>
                {enErrors.length > 0 && (
                  <ENErrorDisplay
                    errors={enErrors}
                    onDismiss={() => setEnErrors([])}
                    className="mb-4"
                  />
                )}
                
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={t('feedback.placeholder')}
                  className="min-h-[120px] mb-2 bg-white"
                />
                <p className="text-[16px] text-gray-600 mb-4">
                  {t('feedback.agreement')}
                </p>
                <button
                  onClick={handleCommentSubmit}
                  disabled={isSubmitting}
                  className="bg-primary text-primary-foreground px-6 py-3 hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-bold"
                >
                  {isSubmitting ? t('feedback.submitting') : t('feedback.submitButton')}
                </button>
              </>
            ) : (
              <div className="bg-green-50 border border-green-200 p-4">
                <p className="text-green-800 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" />
                  {t('feedback.thankYou')}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Donation Section - Hidden for English language */}
        {!hideDonation && (
          <div className="bg-primary/10 border border-primary/30 p-8 mb-8">
            <Heart className="w-12 h-12 text-primary mx-auto mb-4" />
            <h2 className="mb-4 text-[20px] font-bold">{t('donation.title')}</h2>
            <p className="mb-6">
              {t('donation.description')}
            </p>
            <a 
              href={t('donation.url')}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-primary text-primary-foreground px-8 py-4 hover:bg-primary/90 transition-colors font-bold"
            >
              {t('donation.button')}
            </a>
          </div>
        )}

        {/* Social Sharing */}
        <div className="bg-white border border-border p-8 mb-8">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Share2 className="w-6 h-6 text-primary" />
            <h2 className="font-bold text-[20px]">{t('sharing.title')}</h2>
          </div>
          <p className="mb-6">
            {t('sharing.description')}
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={() => handleShare('facebook')}
              className="flex items-center gap-2 bg-[#1877F2] text-white px-6 py-3 hover:bg-[#1877F2]/90 transition-colors font-bold cursor-pointer"
            >
              <Facebook className="w-5 h-5" />
              {t('sharing.buttons.facebook')}
            </button>
            <button
              onClick={() => handleShare('twitter')}
              className="flex items-center gap-2 bg-black text-white px-6 py-3 hover:bg-black/90 transition-colors font-bold cursor-pointer"
            >
              <img 
                src="https://ciwf-consultation-wizard.ams3.digitaloceanspaces.com/app/assets/logo.svg" 
                alt="X" 
                className="w-5 h-5"
              />
              {t('sharing.buttons.twitter')}
            </button>
            <button
              onClick={() => handleShare('email')}
              className="flex items-center gap-2 bg-secondary text-secondary-foreground px-6 py-3 hover:bg-secondary/90 transition-colors font-bold cursor-pointer"
            >
              <Mail className="w-5 h-5" />
              {t('sharing.buttons.email')}
            </button>
          </div>
        </div>

        {/* Return to CIWF Website */}
        <div className="text-center">
          <a 
            href={t('returnLinkUrl')}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block text-[#4D2200] underline font-bold text-[16px] cursor-pointer"
          >
            {t('returnLink')}
          </a>
        </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
